import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { useForm } from "@tanstack/react-form";

import { AsyncPaginate } from "react-select-async-paginate";

import { addProductSchema } from "@/zod-schemas/products/add-product-schema";
import { toProductRequest } from "@/mappers/products/product-form-to-request";

import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { RequiredInput } from "../common/required-input";
import { Separator } from "@/components/ui/separator";

import { toast } from "sonner";
import { useState } from "react";

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { UNIT_OPTIONS } from "@/constants/products/products-constants";

import { useProductCategoryLoadOptions } from "@/hooks/products/use-product-categories";
import { useProductBrandLoadOptions } from "@/hooks/products/use-product-brands";
import { useUpdateProduct } from "@/hooks/products/use-products";
import type { Product } from "@/types/products/api-types/product-types";
import { getProductFormValues } from "@/default-form-values/products/default-product-form-values";

interface EditProductDialogProps {
  product: Product;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditProductDialog({
  product,
  open,
  onOpenChange,
}: EditProductDialogProps) {
  const [serverError, setServerError] = useState<string | null>(null);
  const categoryOptions = useProductCategoryLoadOptions();
  const brandOptions = useProductBrandLoadOptions();
  const updateProduct = useUpdateProduct();

  const form = useForm({
    defaultValues: getProductFormValues(product),
    validators: {
      onSubmit: addProductSchema,
    },
    onSubmit: async ({ value }) => {
      try {
        const validated = addProductSchema.parse(value);
        await updateProduct.mutateAsync({
          id: product.id,
          data: toProductRequest(validated),
        });
        toast.success(`Product "${validated.name}" updated successfully`);
        onOpenChange(false);
      } catch (error: any) {
        const statusCode = error?.response?.status;

        if (statusCode === 409) {
          setServerError(
            `A product with the name "${value.name}" already exists`
          );
        } else {
          toast.error(`Failed to update product "${value.name}"`);
          onOpenChange(false);
        }
      }
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        onOpenChange(isOpen);
        if (!isOpen) {
          form.reset();
          setServerError(null);
        }
      }}
    >
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit Product</DialogTitle>
        </DialogHeader>
        <Separator />

        <form
          className="mb-4"
          id="edit-product-form"
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
        >
          <FieldGroup>
            <form.Field
              name="category"
              children={(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;

                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>
                      Category
                      <RequiredInput />
                    </FieldLabel>
                    <AsyncPaginate
                      value={field.state.value}
                      onChange={field.handleChange}
                      loadOptions={categoryOptions}
                      aria-invalid={isInvalid}
                      className="w-full"
                      placeholder="Select..."
                    />

                    {isInvalid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                  </Field>
                );
              }}
            />
          </FieldGroup>

          <Separator className="my-4" />

          <FieldGroup>
            <form.Field
              name="name"
              children={(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;
                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>
                      Name
                      <RequiredInput />
                    </FieldLabel>
                    <Input
                      id={field.name}
                      name={field.name}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => {
                        field.handleChange(e.target.value);
                        setServerError(null);
                      }}
                      aria-invalid={isInvalid}
                      autoComplete="off"
                    />
                    {isInvalid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                    {serverError && <FieldError>{serverError}</FieldError>}
                  </Field>
                );
              }}
            />

            <form.Field
              name="brand"
              children={(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;

                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>
                      Brand
                      <RequiredInput />
                    </FieldLabel>
                    <AsyncPaginate
                      value={field.state.value}
                      onChange={field.handleChange}
                      loadOptions={brandOptions}
                      aria-invalid={isInvalid}
                      className="w-full"
                      placeholder="Select..."
                    />

                    {isInvalid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                  </Field>
                );
              }}
            />
          </FieldGroup>

          <Separator className="my-4" />

          <FieldGroup>
            <div className="grid grid-cols-2 gap-4">
              <form.Field
                name="size_value"
                children={(field) => {
                  const isInvalid =
                    field.state.meta.isTouched && !field.state.meta.isValid;
                  return (
                    <Field data-invalid={isInvalid}>
                      <FieldLabel htmlFor={field.name}>
                        Size
                        <RequiredInput />
                      </FieldLabel>
                      <Input
                        id={field.name}
                        name={field.name}
                        type="number"
                        step="0.01"
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value)}
                        aria-invalid={isInvalid}
                        autoComplete="off"
                      />
                      {isInvalid && (
                        <FieldError errors={field.state.meta.errors} />
                      )}
                    </Field>
                  );
                }}
              />

              <form.Field
                name="size_unit"
                children={(field) => {
                  const isInvalid =
                    field.state.meta.isTouched && !field.state.meta.isValid;
                  return (
                    <Field data-invalid={isInvalid}>
                      <FieldLabel htmlFor={field.name}>
                        Unit
                        <RequiredInput />
                      </FieldLabel>
                      <Select
                        name={field.name}
                        value={field.state.value === "" ? undefined : field.state.value}
                        onValueChange={(value) => field.handleChange(value)}
                        aria-invalid={isInvalid}
                      >
                        <SelectTrigger id={field.name} className="w-full">
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            {UNIT_OPTIONS.map((unit) => (
                              <SelectItem key={unit.value} value={unit.value}>
                                {unit.label}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>

                      {isInvalid && (
                        <FieldError errors={field.state.meta.errors} />
                      )}
                    </Field>
                  );
                }}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <form.Field
                name="cost_price"
                children={(field) => {
                  const isInvalid =
                    field.state.meta.isTouched && !field.state.meta.isValid;
                  return (
                    <Field data-invalid={isInvalid}>
                      <FieldLabel htmlFor={field.name}>
                        Cost Price <RequiredInput />
                      </FieldLabel>
                      <div className="relative">
                        <span
                          aria-hidden="true"
                          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base md:text-sm"
                        >
                          $
                        </span>
                        <Input
                          id={field.name}
                          name={field.name}
                          type="number"
                          step="0.01"
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                          aria-invalid={isInvalid}
                          autoComplete="off"
                          className="pl-6"
                        />
                      </div>
                      <FieldDescription>Ex. GST</FieldDescription>
                      {isInvalid && (
                        <FieldError errors={field.state.meta.errors} />
                      )}
                    </Field>
                  );
                }}
              />

              <form.Field
                name="retail_price"
                children={(field) => {
                  const isInvalid =
                    field.state.meta.isTouched && !field.state.meta.isValid;
                  return (
                    <Field data-invalid={isInvalid}>
                      <FieldLabel htmlFor={field.name}>
                        Retail Price <RequiredInput />
                      </FieldLabel>
                      <div className="relative">
                        <span
                          aria-hidden="true"
                          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base md:text-sm"
                        >
                          $
                        </span>
                        <Input
                          id={field.name}
                          name={field.name}
                          type="number"
                          step="0.01"
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                          aria-invalid={isInvalid}
                          autoComplete="off"
                          className="pl-6"
                        />
                      </div>
                      <FieldDescription>Inc. GST</FieldDescription>
                      {isInvalid && (
                        <FieldError errors={field.state.meta.errors} />
                      )}
                    </Field>
                  );
                }}
              />
            </div>
          </FieldGroup>
        </form>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" className="w-24">
              Cancel
            </Button>
          </DialogClose>
          <Button type="submit" form="edit-product-form" className="w-24">
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
